import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { BackgroundLayout } from './components/BackgroundLayout';

const MAX_COMMENT_LENGTH = 2500;

export const Message = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const flow = stores.reviewFlow;
  const parentId = pathId ? Number(pathId) : undefined;

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Your Message';
  }, []);

  useEffect(() => {
    if (!parentId) return;
    if (!flow.isValidForParent(parentId)) {
      navigate(`/parent/${parentId}/feedback`, { replace: true });
    }
  }, [parentId, flow, navigate]);

  const review = flow.review;
  const comment = review?.comment ?? '';
  const remaining = MAX_COMMENT_LENGTH - comment.length;

  const submit = async (): Promise<void> => {
    if (submitting || !parentId) return;
    setSubmitting(true);
    try {
      if (review?.id) {
        await flow.saveReview();
      }
      navigate(`/parent/${parentId}/feedback/availability`);
    } catch {
      toast.error(t('pages.parentFeedback.message.submitFailed'));
      setSubmitting(false);
    }
  };

  return (
    <BackgroundLayout id='parent-review'>
      <h1 className='text-2xl font-semibold mb-6'>
        {t('pages.parentFeedback.message.heading')}
      </h1>

      <div className='mb-6'>
        <textarea
          className='w-full border border-neutral-300 rounded p-2 focus:ring-sapphire focus:border-sapphire'
          rows={16}
          maxLength={MAX_COMMENT_LENGTH}
          value={comment}
          onChange={(e): void => flow.setReview({ comment: e.target.value })}
          placeholder={t('pages.parentFeedback.message.placeholder')}
        />
        <div className='text-right text-xs text-neutral-500 mt-1'>
          {t('pages.parentFeedback.message.charactersRemaining', { count: remaining })}
        </div>
      </div>

      <div className='text-center mt-5'>
        <Button
          buttonType={ButtonType.primary}
          onClick={submit}
          disabled={submitting}
        >
          {t('pages.parentFeedback.message.next')}
        </Button>
      </div>
    </BackgroundLayout>
  );
});
